const REVIEW_MODE_STORAGE_PREFIX = 'dcf_teacher_stock_review_mode';

export const DEFAULT_REVIEW_MODE_STATE = Object.freeze({
  enabled: false,
  sinceDate: null,
});

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isValidCalendarDate(value) {
  if (typeof value !== 'string' || !DATE_PATTERN.test(value)) return false;

  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(timestamp)
    && new Date(timestamp).toISOString().slice(0, 10) === value;
}

function getTodayDateString() {
  const today = new Date();
  const year = String(today.getFullYear()).padStart(4, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseReviewSinceDate(value) {
  const candidate = typeof value === 'string' ? value.trim() : '';
  if (!isValidCalendarDate(candidate) || candidate > getTodayDateString()) {
    return null;
  }
  return candidate;
}

export function isOnOrBeforeDate(itemDate, sinceDate) {
  return isValidCalendarDate(itemDate)
    && isValidCalendarDate(sinceDate)
    && itemDate <= sinceDate;
}

export function isAfterDate(itemDate, sinceDate) {
  return isValidCalendarDate(itemDate)
    && isValidCalendarDate(sinceDate)
    && itemDate > sinceDate;
}

export function getReviewModeStorageKey(ticker) {
  const normalizedTicker = typeof ticker === 'string' ? ticker.trim().toUpperCase() : '';
  return `${REVIEW_MODE_STORAGE_PREFIX}:${normalizedTicker}`;
}

function getBrowserStorage() {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function normalizeReviewModeState(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_REVIEW_MODE_STATE };
  }

  return {
    enabled: value.enabled === true,
    sinceDate: parseReviewSinceDate(value.sinceDate),
  };
}

export function readReviewModeState(ticker, storage = getBrowserStorage()) {
  try {
    const storedValue = storage?.getItem(getReviewModeStorageKey(ticker));
    if (!storedValue) return { ...DEFAULT_REVIEW_MODE_STATE };
    return normalizeReviewModeState(JSON.parse(storedValue));
  } catch {
    return { ...DEFAULT_REVIEW_MODE_STATE };
  }
}

export function writeReviewModeState(ticker, value, storage = getBrowserStorage()) {
  const normalizedState = normalizeReviewModeState(value);
  try {
    storage?.setItem(
      getReviewModeStorageKey(ticker),
      JSON.stringify(normalizedState),
    );
  } catch {
    // Review mode remains usable in memory when localStorage is unavailable.
  }
  return normalizedState;
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function resolvePlottedPrice(price, preferAdjustedClose) {
  const adjustedClose = toFiniteNumber(price?.adj_close);
  const close = toFiniteNumber(price?.close);
  if (preferAdjustedClose && adjustedClose !== null) {
    return adjustedClose;
  }
  return close;
}

/**
 * Compare the last trading day on/before `sinceDate` to the latest available price.
 * Returns null when a baseline or latest price cannot be resolved.
 */
export function computeStockPriceChangeSinceDate(
  prices,
  sinceDate,
  { preferAdjustedClose = true } = {},
) {
  const normalizedSinceDate = parseReviewSinceDate(sinceDate);
  if (!normalizedSinceDate || !Array.isArray(prices) || prices.length === 0) {
    return null;
  }

  const rows = prices
    .flatMap((price) => {
      const tradingDate = typeof price?.trading_date === 'string'
        ? price.trading_date.slice(0, 10)
        : null;
      const plottedPrice = resolvePlottedPrice(price, preferAdjustedClose);
      if (!isValidCalendarDate(tradingDate) || plottedPrice === null) {
        return [];
      }
      return [{ tradingDate, price: plottedPrice }];
    })
    .toSorted((left, right) => (
      left.tradingDate < right.tradingDate
        ? -1
        : left.tradingDate > right.tradingDate
          ? 1
          : 0
    ));

  if (!rows.length) {
    return null;
  }

  let baseline = null;
  for (const row of rows) {
    if (row.tradingDate <= normalizedSinceDate) {
      baseline = row;
    }
  }

  const latest = rows[rows.length - 1];
  if (!baseline || baseline.price === 0) {
    return null;
  }

  const absoluteChange = latest.price - baseline.price;
  return {
    baselineDate: baseline.tradingDate,
    baselinePrice: baseline.price,
    latestDate: latest.tradingDate,
    latestPrice: latest.price,
    absoluteChange,
    percentChange: absoluteChange / baseline.price,
  };
}
